import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronRightIcon, ArrowRightIcon } from '@heroicons/react/24/outline'
import { FREE_TOOLS } from '@/data/tools'
import { TOOL_ICONS } from '@/components/tools/toolUi'

/**
 * Visible trail back to the hub. The pages already emit BreadcrumbList JSON-LD;
 * this gives people the same way back that search engines get.
 */
export function ToolBreadcrumb({ path }: { path: string }) {
    const { t } = useTranslation()
    const tool = FREE_TOOLS.find((item) => item.path === path)
    return (
        <nav aria-label={t('tools.common.breadcrumb')} className="mb-5 flex justify-center">
            <ol className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
                <li>
                    <Link to="/tools" className="rounded hover:text-primary-600 dark:hover:text-primary-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500">
                        {t('nav.public.freeTools')}
                    </Link>
                </li>
                <li aria-hidden="true"><ChevronRightIcon className="h-3.5 w-3.5" /></li>
                <li aria-current="page" className="font-medium text-gray-700 dark:text-gray-300">
                    {tool ? t(tool.nameKey) : null}
                </li>
            </ol>
        </nav>
    )
}

/** Cross-links to the other tools, so a visitor who finished one has a next step. */
export function MoreTools({ currentPath }: { currentPath: string }) {
    const { t } = useTranslation()
    const others = FREE_TOOLS.filter((tool) => tool.path !== currentPath)
    return (
        <section aria-labelledby="more-tools-heading" className="max-w-5xl mx-auto mt-16 px-4 sm:px-6 lg:px-8">
            <div className="flex items-baseline justify-between gap-4 mb-4">
                <h2 id="more-tools-heading" className="text-xl font-bold text-gray-900 dark:text-white">
                    {t('tools.common.moreTools')}
                </h2>
                <Link to="/tools" className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500">
                    {t('nav.public.viewAllTools')}
                </Link>
            </div>
            <ul className="grid gap-3 sm:grid-cols-2">
                {others.map((tool) => {
                    const Icon = TOOL_ICONS[tool.icon]
                    return (
                        <li key={tool.path}>
                            <Link
                                to={tool.path}
                                className="group flex items-center gap-4 h-full p-4 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 hover:border-primary-200 dark:hover:border-primary-800 hover:shadow-md transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                            >
                                <span className={`inline-flex shrink-0 items-center justify-center w-10 h-10 rounded-xl ${tool.accent}`}>
                                    <Icon className="w-5 h-5" />
                                </span>
                                <span className="min-w-0 flex-1">
                                    <span className="block text-sm font-semibold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                                        {t(tool.nameKey)}
                                    </span>
                                    <span className="block text-xs text-gray-500 dark:text-gray-400">{t(tool.outcomeKey)}</span>
                                </span>
                                <ArrowRightIcon className="w-4 h-4 shrink-0 text-gray-400 group-hover:translate-x-0.5 group-hover:text-primary-500 transition-all" />
                            </Link>
                        </li>
                    )
                })}
            </ul>
        </section>
    )
}
